import classnames from 'classnames';
import { HTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTranslate } from '@growtherapy/i18n';
import { Button } from '@growtherapy/sprout-ui';

import { useTheme } from '../hooks';
import { Theme } from '../state';
import { GrowLogo } from './GrowLogo';
import { LanguageSwitcherWrapper as LanguageSwitcher } from './LanguageSwitcher';

type NavbarProps = {
  className?: string;
  onClickNeedHelpButton?: () => void;
  TranscriptionPillComponent?: React.FC;
  showLanguageSwitcher?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export function Navbar({
  className,
  onClickNeedHelpButton,
  TranscriptionPillComponent,
  showLanguageSwitcher,
  ...props
}: NavbarProps): JSX.Element {
  const t = useTranslate();
  const { theme } = useTheme();
  const showNeedHelp = !!onClickNeedHelpButton;
  return (
    <div
      className={twMerge(
        classnames(
          {
            'bg-neutral-300': theme === Theme.LIGHT,
            'bg-neutral-800': theme === Theme.DARK,
          },
          'relative flex items-center justify-between px-6 py-7 h-18',
        ),
        className,
      )}
      {...props}
    >
      <div className='flex content-start items-center gap-4'>
        <GrowLogo />
        {TranscriptionPillComponent && <TranscriptionPillComponent />}
      </div>
      <div className='flex items-center gap-350 sm:gap-450'>
        {showLanguageSwitcher && <LanguageSwitcher />}
        {showNeedHelp && (
          <Button
            use='unstyled'
            className={twMerge(
              classnames({
                'text-lilac-700 [&_span]:rebrand:text-action-primary-textLink': theme === Theme.LIGHT,
                'text-neutral-100 [&_span]:rebrand:!text-text-inverse': theme === Theme.DARK,
              }),
              'px-0 py-0 rebrand:px-0 rebrand:py-0 underline hover:brightness-85 active:brightness-70',
            )}
            onClick={onClickNeedHelpButton}
          >
            <span className={classnames('hidden sm:block', { underline: showLanguageSwitcher })}>
              {t('telehealth-core:navigation.needHelp')}
            </span>
            <span className={classnames('block sm:hidden', { underline: showLanguageSwitcher })}>?</span>
          </Button>
        )}
      </div>
    </div>
  );
}
